<template>
  <BaseTable
    v-if="matrices"
    :headers="matricesHeaders"
    :list="matrices"
    :hide-grouping="true"
    @add-new="emit('matrixDetails', uuidv4())"
    @deleteMultiselect="onDeleteMultiselect"
    @refresh-data="loadData"
    clickable
    @table-item-clicked="onMatrixClicked"
    table-id="matrices-table">
    <template #actions="{ item }: any">
      <div class="flex items-center justify-center gap-1">
        <button
          v-if="item.isFromManager"
          type="button"
          class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
          @click="onDeleteRow(item as PricingPolicyMatrix)">
          <IconTrashOutline class="size-4" />
        </button>
      </div>
    </template>
  </BaseTable>

  <BaseModalConfirm
    v-if="modalDeleteRow"
    @cancel="modalDeleteRow = false"
    @confirm="onDeleteSingle()" />
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useToastStore } from '@/stores/toast';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import { getPricingPolicyMatrices, removeMatricesByIds } from '@/services/api/prices';
import { filterData, sortData } from '@/utils/table';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';

import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';

import type { TableFilterData } from '@/types/Base';
import type { PricingPolicyMatrix } from '@/types/PricingTypes';

const { t: $t } = useI18n({ useScope: 'global' });

const toastStore = useToastStore();

const emit = defineEmits(['matrixDetails']);

const matrices = ref<Partial<PricingPolicyMatrix>[]>([]);
const matricesHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  {
    value: 'pricingPolicyCatalogOrderHeader.name',
    text: $t('pricing-catalogs.catalog'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  await loadData();
});

async function loadData(data?: TableFilterData) {
  let response = await getPricingPolicyMatrices();
  if (!response) return;
  if (data && data.query)
    response = filterData(response, data.query, [
      'name',
      'code',
      'pricingPolicyCatalogOrderHeader.name',
      'pricingPolicyCatalogOrderHeader.code',
    ]);
  if (data && data.column !== undefined && data.order)
    response = sortData(response, data.column, data.order, matricesHeaders);
  matrices.value = response;
}

async function onDeleteMultiselect(items: PricingPolicyMatrix[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  const res = await removeMatricesByIds(idsToDelete);
  if (!res && matrices.value)
    matrices.value = matrices.value.filter((route) => !route.id || !idsToDelete.includes(route.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeMatricesByIds([idToDelete]);
  if (!res && matrices.value)
    matrices.value = matrices.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<PricingPolicyMatrix>();
function onDeleteRow(item: PricingPolicyMatrix) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onMatrixClicked(item: PricingPolicyMatrix) {
  emit('matrixDetails', item.id);
}
</script>
